import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';
import useTicketEventData from '../../hooks/useTicketEventData';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import './PublicTicketBookingPage.css';

const formatRupiah = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const STEPS = ['Select Winner', 'Select Seats & Add-ons', 'Your Details', 'Review & Pay'];

export default function PublicTicketBookingPage() {
  const { currentEventId } = useAuth();
  const navigate = useNavigate();
  const { event, loading: eventLoading, error: eventError } = useTicketEventData();

  const dynamicVenueInfo = useMemo(() => {
    const info = {};
    (event?.venues || []).forEach(v => {
      info[v.id] = { label: v.label, img: v.imageUrl };
    });
    return info;
  }, [event]);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const topRef = useRef(null);

  // Step 0 - Winner Selection
  const [winners, setWinners] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWinner, setSelectedWinner] = useState(null);

  const [buyerType, setBuyerType] = useState('public'); // 'public' or 'winner'
  const [orchestraSessionId, setOrchestraSessionId] = useState('');

  const [venue, setVenue] = useState('');
  const [date, setDate] = useState('');
  const [session, setSession] = useState('');

  const orchestraSessions = useMemo(() => event?.orchestraSessions || [], [event]);
  const selectedOrchestraSession = useMemo(() => orchestraSessions.find(s => s.id === orchestraSessionId), [orchestraSessions, orchestraSessionId]);

  useEffect(() => {
    if (selectedOrchestraSession) {
      setVenue(selectedOrchestraSession.venue);
      setDate(selectedOrchestraSession.date);
      setSession(selectedOrchestraSession.time);
    }
  }, [selectedOrchestraSession]);

  const complimentaryGranted = useMemo(() => {
    if (buyerType !== 'winner' || !selectedOrchestraSession) return false;
    const quota = selectedOrchestraSession.complimentaryQuota || 0;
    const claimed = selectedOrchestraSession.complimentaryClaimed || 0;
    return (quota - claimed) >= 2;
  }, [buyerType, selectedOrchestraSession]);

  const complimentaryCount = complimentaryGranted ? 2 : 0;

  // Step 1 – seat picking & add-ons
  const [seatLayout, setSeatLayout] = useState([]);
  const [seatLayoutLoading, setSeatLayoutLoading] = useState(false);
  const [selections, setSelections] = useState({});   // { tierId: [seatObj] }
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  // Step 2
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: 'smooth' });

  // Fetch eligible winners on mount
  useEffect(() => {
    const fetchWinners = async () => {
      setWinnersLoading(true);
      try {
        const res = await apis.publicTicket.getEligibleWinners();
        console.log("Eligible winners: ", res);
        if (res?.error) throw new Error('Failed to load winners.');
        setWinners(res.data?.winners || []);
      } catch (err) {
        console.error('Fetch winners error', err);
      } finally {
        setWinnersLoading(false);
      }
    };
    fetchWinners();
  }, []);

  // Fetch live seat layout when entering step 1
  useEffect(() => {
    if (step !== 1 || !venue || !date || !session) return;
    const fetchSeats = async () => {
      setSeatLayoutLoading(true);
      try {
        const sessionId = `${date}_${session}`;
        const res = await apis.publicTicket.getSeats(venue, sessionId);
        if (res?.error) throw new Error('Failed to load seats.');
        setSeatLayout(res.data || []);
      } catch (e) {
        console.error('Seat fetch error', e);
      } finally {
        setSeatLayoutLoading(false);
      }
    };
    fetchSeats();
  }, [step, venue, date, session]);

  const tierColors = useMemo(() => ({
    'presto': '#EBBC64', // Gold
    'allegro': '#C0C0C0', // Silver
    'lento': '#CD7F32', // Bronze
  }), []);

  const rightSideMax = useMemo(() => ({
    A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
    G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
    N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
  }), []);

  // Format seat layout as a single unified theater map
  const unifiedLayout = useMemo(() => {
    if (!seatLayout.length) return [];

    // Group all seats by row first, ignoring tier
    const byRow = seatLayout.reduce((acc, s) => {
      if (!acc[s.row]) acc[s.row] = [];
      acc[s.row].push(s);
      return acc;
    }, {});

    // Sort rows alphabetically (A, B, C...)
    const sortedRowKeys = Object.keys(byRow).sort((a, b) => a.localeCompare(b));

    return sortedRowKeys.map(row => {
      // Sort seats within row by number descending (right to left)
      byRow[row].sort((a, b) => b.number - a.number);

      const mappedRow = byRow[row].map(s => {
        const isReservedRow = selectedOrchestraSession?.reservedRows?.includes(s.row);
        return {
          id: s.id, number: s.number, row: s.row, areaType: s.areaType, seatLabel: s.seatLabel,
          isReserved: s.status !== 'available' || isReservedRow,
          color: isReservedRow ? '#444444' : (tierColors[s.areaType] || '#888888'),
          tooltip: isReservedRow ? `${s.seatLabel} – Reserved for Registrants` : `${s.seatLabel} – ${s.status !== 'available' ? 'Taken' : 'Available'}`,
        };
      });

    const splitSeatNumber = rightSideMax[row];
    if (splitSeatNumber) {
      const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
      if (splitIndex !== -1) {
        // Insert two nulls for a wide aisle
        mappedRow.splice(splitIndex, 0, null, null);
      }
    }

    return mappedRow;
  }).filter(r => r.length > 0);
}, [seatLayout, tierColors, rightSideMax]);

// Derived quantities from selections
const quantities = useMemo(() => {
  const q = {};
  (event?.ticketTiers || []).forEach(t => {
    q[t.id] = (selections[t.id] || []).length;
  });
  return q;
}, [event, selections]);

// Total seats selected
const totalSelected = useMemo(() =>
  Object.values(selections).reduce((s, arr) => s + arr.length, 0),
  [selections]
);

// All selected seat IDs (flat)
const allSelectedSeatIds = useMemo(() =>
  Object.values(selections).flat().map(s => s.id),
  [selections]
);

// Order total
const orderTotal = useMemo(() => {
  let total = 0;
  (event?.ticketTiers || []).forEach(t => {
    total += (quantities[t.id] || 0) * t.price;
  });
  (event?.addOns || []).forEach(a => {
    if (selectedAddOns.includes(a.id)) total += a.price;
  });
  return total;
}, [event, quantities, selectedAddOns]);

// Build tickets array for submission
const ticketsPayload = useMemo(() =>
  (event?.ticketTiers || [])
    .filter(t => (quantities[t.id] || 0) > 0)
    .map(t => ({ id: t.id, name: t.name, quantity: quantities[t.id], priceEach: t.price })),
  [event, quantities]
);

// --- Navigation guards ---
const canProceedStep0 = buyerType === 'public'
  ? !!orchestraSessionId
  : (!!selectedWinner && !!orchestraSessionId);
const canProceedStep1 = true; // They can proceed with 0 seats if they have complimentary tickets
const canProceedStep2 = userName.trim() && userEmail.trim() && userPhone.trim();

const goNext = () => { setStep(s => s + 1); scrollTop(); };
const goBack = () => { setStep(s => s - 1); scrollTop(); };

const handleSelectWinner = (winner) => {
  setSelectedWinner(winner);
  // Pre-fill user details (can be edited in Step 2)
  setUserName(winner.name || '');
  setUserEmail(winner.email || '');
};

const filteredWinners = useMemo(() => {
  if (!searchTerm) return winners;
  const lower = searchTerm.toLowerCase();
  return winners.filter(w =>
    w.name?.toLowerCase().includes(lower) ||
    w.teacherName?.toLowerCase().includes(lower)
  );
}, [winners, searchTerm]);

const handleSelectSeat = (seat) => {
  const tierId = seat.areaType;
  setSelections(p => ({ ...p, [tierId]: [...(p[tierId] || []), seat] }));
};
const handleDeselectSeat = (seat) => {
  const tierId = seat.areaType;
  setSelections(p => ({ ...p, [tierId]: (p[tierId] || []).filter(s => s.id !== seat.id) }));
};

const handleSubmit = async () => {
  setIsSubmitting(true);
  setSubmitError('');
  try {
    const payload = {
      userName, userEmail, userPhone,
      venue, date, session,
      orchestraSessionId,
      isPublic: buyerType === 'public',
      complimentaryTickets: complimentaryCount,
      tickets: ticketsPayload,
      selectedSeatIds: allSelectedSeatIds,
      addOnIds: selectedAddOns,
      registrantId: selectedWinner?.registrantId,
    };
    const res = await apis.publicTicket.createBooking(payload);
    if (res?.error) throw new Error(res.error?.data?.message || 'Booking failed.');
    const { bookingId, paymentUrl } = res.data;
    window.open(paymentUrl, '_blank');
    navigate(`/waiting-payment/${bookingId}`, { state: { paymentLink: paymentUrl } });
  } catch (err) {
    setSubmitError(err.response?.data?.message || err.message || 'Booking failed. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

if (eventLoading) return (
  <div className="ptb-loading">
    <div className="ptb-spinner" />
    <p>Loading event details…</p>
  </div>
);

if (eventError) return (
  <div className="ptb-error">
    <p>Unable to load event data. Please refresh the page.</p>
  </div>
);

const availableDates = event?.sessions?.[venue] ? Object.keys(event.sessions[venue]) : [];
const availableSessions = (venue && date && event?.sessions?.[venue]?.[date]) || [];

return (
  <div className="ptb-root" ref={topRef}>
    {/* ── Google Fonts ── */}
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500&display=swap" rel="stylesheet" />

    {/* ── Header ── */}
    <header className="ptb-header">
      <div className="ptb-header-inner">
        <span className="ptb-brand">APCS <span className="ptb-brand-accent">2026</span></span>
        <span className="ptb-subtitle">Gala Concert Tickets</span>
      </div>
    </header>

    {/* ── Progress ── */}
    <div className="ptb-stepper">
      {STEPS.map((label, i) => (
        <div key={i} className={`ptb-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
          <div className="ptb-step-circle">{i < step ? '✓' : i + 1}</div>
          <span className="ptb-step-label">{label}</span>
          {i < STEPS.length - 1 && <div className="ptb-step-line" />}
        </div>
      ))}
    </div>

    {/* ── Content ── */}
    <main className="ptb-main">

      {/* STEP 0 — Select Winner */}
      {step === 0 && (
        <div className="ptb-card ptb-animate">
          <h2 className="ptb-card-title">Identify Your Entry</h2>
          <p className="ptb-card-subtitle">Please select the registered winner to proceed with ticket booking.</p>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
            <button
              className={`ptb-btn-ghost ${buyerType === 'public' ? 'active-tab' : ''}`}
              onClick={() => setBuyerType('public')}
              style={{ flex: 1, backgroundColor: buyerType === 'public' ? '#EBBC64' : 'transparent', color: buyerType === 'public' ? '#000' : '#EBBC64' }}
            >
              Public Buyer
            </button>
            <button
              className={`ptb-btn-ghost ${buyerType === 'winner' ? 'active-tab' : ''}`}
              onClick={() => setBuyerType('winner')}
              style={{ flex: 1, backgroundColor: buyerType === 'winner' ? '#EBBC64' : 'transparent', color: buyerType === 'winner' ? '#000' : '#EBBC64' }}
            >
              Registered Winner
            </button>
          </div>

          {buyerType === 'winner' && (
            <>
              <div className="ptb-search-box">
                <SearchOutlined className="ptb-search-icon" />
                <input
                  type="text"
                  placeholder="Search by your name or teacher..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="ptb-search-input"
                />
              </div>

              {winnersLoading ? (
                <div className="ptb-winner-loading">
                  <div className="ptb-spinner-sm" />
                  <p>Loading eligible winners...</p>
                </div>
              ) : (
                <div className="ptb-winner-grid">
                  {filteredWinners.length > 0 ? (
                    filteredWinners.map(w => (
                      <div
                        key={w.registrantId}
                        className={`ptb-winner-card ${selectedWinner?.registrantId === w.registrantId ? 'selected' : ''}`}
                        onClick={() => handleSelectWinner(w)}
                      >
                        <div className="ptb-winner-info">
                          <div className="ptb-winner-header">
                            <span className={`ptb-award-tag ${w.finalAward.toLowerCase()}`}>
                              {w.finalAward}
                            </span>
                            <span className="ptb-winner-category">{w.competitionCategory}</span>
                          </div>
                          <h3 className="ptb-winner-name">{w.name}</h3>
                          <p className="ptb-winner-teacher">🎓 {w.teacherName}</p>
                        </div>
                        <div className="ptb-winner-assignment">
                          <div className="ptb-assignment-row">
                            <span className="ptb-label">Venue:</span>
                            <span className="ptb-value">{dynamicVenueInfo[w.session?.venue]?.label || w.session?.venue}</span>
                          </div>
                          <div className="ptb-assignment-row">
                            <span className="ptb-label">Session:</span>
                            <span className="ptb-value">{w.session?.date} | {w.session?.time}</span>
                          </div>
                        </div>
                        {selectedWinner?.registrantId === w.registrantId && (
                          <div className="ptb-selected-check">✓ Selected</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="ptb-no-results">
                      <UserOutlined style={{ fontSize: '2rem', color: '#444', marginBottom: '1rem' }} />
                      <p>No winners found matching your search.</p>
                      <p className="ptb-help-text">Can't find your name? Please contact our admin for assistance.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Select Orchestra Session</h3>
            <select
              className="ptb-input"
              value={orchestraSessionId}
              onChange={(e) => setOrchestraSessionId(e.target.value)}
            >
              <option value="">-- Choose a Session --</option>
              {orchestraSessions.map(os => (
                <option key={os.id} value={os.id}>
                  {os.venue} | {os.date} | {os.time}
                </option>
              ))}
            </select>
          </div>

          <div className="ptb-actions" style={{ marginTop: '2rem' }}>
            <button className="ptb-btn-primary" disabled={!canProceedStep0} onClick={goNext}>
              Continue to Seats →
            </button>
          </div>
        </div>
      )}

      {/* STEP 1 — Seat Selection & Add-ons */}
      {step === 1 && (
        <div className="ptb-card ptb-animate ptb-step-fused">
          <h2 className="ptb-card-title">Select Seats & Add-ons</h2>

          {complimentaryGranted && (
            <div style={{ padding: '1rem', backgroundColor: '#2d3748', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #EBBC64' }}>
              <h3 style={{ color: '#EBBC64', margin: 0 }}>🎉 2 Complimentary Tickets Granted!</h3>
              <p style={{ margin: '0.5rem 0 0 0', color: '#e2e8f0' }}>Your seats for these 2 free tickets will be assigned on-the-spot. You do not need to pick them on the map. You can optionally purchase additional tickets from the map below.</p>
            </div>
          )}

          {buyerType === 'winner' && !complimentaryGranted && (
            <div style={{ padding: '1rem', backgroundColor: '#742a2a', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fc8181' }}>
              <p style={{ margin: 0, color: '#fed7d7' }}>Notice: Complimentary tickets for this session have been fully claimed. You can still purchase tickets at the normal price.</p>
            </div>
          )}

          <div className="ptb-fused-layout">
            <div className="ptb-fused-main">
              {seatLayoutLoading ? (
                <div className="ptb-seat-loading"><div className="ptb-spinner" /><p>Loading seat map…</p></div>
              ) : (
                unifiedLayout.length > 0 && (
                  <div className="ptb-tier-section ptb-tier-section-fused">
                    <div className="ptb-tier-header">
                      <h3 className="ptb-section-label">Venue Map: {dynamicVenueInfo[venue]?.label}</h3>
                    </div>
                    {dynamicVenueInfo[venue]?.img && (
                      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <img src={dynamicVenueInfo[venue].img} alt="Venue Map" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #444' }} />
                      </div>
                    )}
                    <div className="ptb-seat-scroll">
                      <CustomSeatPicker
                        layout={unifiedLayout}
                        selectedSeats={Object.values(selections).flat()}
                        onSelect={handleSelectSeat}
                        onDeselect={handleDeselectSeat}
                        maxSeats={Infinity}
                        onSeatClick={null}
                      />
                    </div>
                  </div>
                )
              )}

              <div className="ptb-seat-legend">
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem', borderBottom: '1px solid #1f1f1f', paddingBottom: '1rem' }}>
                  {(event?.ticketTiers || []).map(t => (
                    <span key={t.id} style={{ display: 'flex', alignItems: 'center', color: '#f0f0f0' }}>
                      <span className="ptb-legend-dot" style={{ backgroundColor: tierColors[t.id] || '#888', marginRight: '6px' }} />
                      {t.name} ({formatRupiah(t.price)})
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot available" /> Available</span>
                  <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot selected" /> Selected</span>
                  <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot taken" /> Taken</span>
                </div>
              </div>

              {(event?.addOns || []).length > 0 && (
                <div className="ptb-addons-section">
                  <h3 className="ptb-section-label" style={{ marginTop: '2rem' }}>Add-ons (Optional)</h3>
                  <div className="ptb-addon-list">
                    {event.addOns.map(a => (
                      <label key={a.id} className={`ptb-addon-item ${selectedAddOns.includes(a.id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedAddOns.includes(a.id)}
                          onChange={() => setSelectedAddOns(p => p.includes(a.id) ? p.filter(x => x !== a.id) : [...p, a.id])}
                        />
                        <span>{a.name}</span>
                        <span className="ptb-addon-price">+{formatRupiah(a.price)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="ptb-fused-sidebar">
              <div className="ptb-cart-summary">
                <h3>Order Summary</h3>
                {totalSelected === 0 && selectedAddOns.length === 0 && complimentaryCount === 0 ? (
                  <p className="ptb-cart-empty">Click on the map to select seats.</p>
                ) : (
                  <div className="ptb-cart-items">
                    {complimentaryCount > 0 && (
                      <div className="ptb-cart-row">
                        <span>{complimentaryCount}x Complimentary Ticket</span>
                        <span>Free</span>
                      </div>
                    )}
                    {(event?.ticketTiers || []).map(tier => {
                      const qty = quantities[tier.id] || 0;
                      if (qty === 0) return null;
                      return (
                        <div key={tier.id} className="ptb-cart-row">
                          <span>{qty}x {tier.name}</span>
                          <span>{formatRupiah(qty * tier.price)}</span>
                        </div>
                      );
                    })}
                    {event?.addOns?.filter(a => selectedAddOns.includes(a.id)).map(a => (
                      <div key={a.id} className="ptb-cart-row ptb-cart-addon">
                        <span>1x {a.name}</span>
                        <span>{formatRupiah(a.price)}</span>
                      </div>
                    ))}
                    <div className="ptb-cart-total">
                      <span>Total</span>
                      <span>{formatRupiah(orderTotal)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ptb-actions">
            <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
            <button className="ptb-btn-primary" disabled={!canProceedStep1} onClick={goNext}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Details */}
      {step === 2 && (
        <div className="ptb-card ptb-animate">
          <h2 className="ptb-card-title">Your Details</h2>
          <div className="ptb-form">
            <label className="ptb-label">Full Name *</label>
            <input className="ptb-input" placeholder="e.g. Budi Santoso" value={userName} onChange={e => setUserName(e.target.value)} />

            <label className="ptb-label">Email Address *</label>
            <input className="ptb-input" type="email" placeholder="email@example.com" value={userEmail} onChange={e => setUserEmail(e.target.value)} />

            <label className="ptb-label">Phone Number *</label>
            <input className="ptb-input" type="tel" placeholder="+62 812 3456 7890" value={userPhone} onChange={e => setUserPhone(e.target.value)} />

            <p className="ptb-hint" style={{ marginTop: '0.5rem' }}>
              Your booking confirmation and payment link will be sent to this email.
            </p>
          </div>

          <div className="ptb-actions">
            <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
            <button className="ptb-btn-primary" disabled={!canProceedStep2} onClick={goNext}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Review & Pay */}
      {step === 3 && (
        <div className="ptb-card ptb-animate">
          <h2 className="ptb-card-title">Review & Pay</h2>

          <div className="ptb-summary">
            <div className="ptb-summary-row"><span>Venue</span><strong>{dynamicVenueInfo[venue]?.label || venue}</strong></div>
            <div className="ptb-summary-row"><span>Date</span><strong>{date}</strong></div>
            <div className="ptb-summary-row"><span>Session</span><strong>{session}</strong></div>
            <hr className="ptb-divider" />
            {buyerType === 'winner' ? (
              <div className="ptb-summary-row"><span>Winner</span><strong>{selectedWinner?.name} <span className={`ptb-award-inline ${selectedWinner?.finalAward.toLowerCase()}`}>({selectedWinner?.finalAward})</span></strong></div>
            ) : (
              <div className="ptb-summary-row"><span>Buyer Type</span><strong>Public Buyer</strong></div>
            )}
            <div className="ptb-summary-row"><span>Buyer Name</span><strong>{userName}</strong></div>
            <div className="ptb-summary-row"><span>Buyer Email</span><strong>{userEmail}</strong></div>
            <hr className="ptb-divider" />

            {complimentaryCount > 0 && (
              <div className="ptb-summary-row">
                <span>{complimentaryCount}× Complimentary Ticket</span>
                <strong>Free</strong>
              </div>
            )}

            {ticketsPayload.map(t => (
              <div key={t.id} className="ptb-summary-row">
                <span>{t.quantity}× {t.name}</span>
                <strong>{formatRupiah(t.quantity * t.priceEach)}</strong>
              </div>
            ))}

            {(event?.addOns || []).filter(a => selectedAddOns.includes(a.id)).map(a => (
              <div key={a.id} className="ptb-summary-row">
                <span>{a.name}</span>
                <strong>{formatRupiah(a.price)}</strong>
              </div>
            ))}

            {(allSelectedSeatIds.length > 0 || complimentaryCount > 0) && (
              <div className="ptb-summary-row ptb-seats-row">
                <span>Seats</span>
                <strong>
                  {complimentaryCount > 0 ? `${complimentaryCount} On-The-Spot Seats` : ''}
                  {complimentaryCount > 0 && allSelectedSeatIds.length > 0 ? ', ' : ''}
                  {Object.values(selections).flat().map(s => s.tooltip?.split(' – ')[0]).join(', ')}
                </strong>
              </div>
            )}

            <hr className="ptb-divider" />
            <div className="ptb-summary-row ptb-total-row">
              <span>Total</span>
              <strong className="ptb-gold">{formatRupiah(orderTotal)}</strong>
            </div>
          </div>

          <div className="ptb-hold-notice">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBBC64" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span>Your seats will be held for <strong>30 minutes</strong> after clicking Pay Now. Complete payment within this window to confirm your booking.</span>
          </div>

          {submitError && <p className="ptb-error-msg">{submitError}</p>}

          <div className="ptb-actions">
            <button className="ptb-btn-ghost" onClick={goBack} disabled={isSubmitting}>← Back</button>
            <button className="ptb-btn-primary ptb-pay-btn" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <span className="ptb-spinner-sm" /> : null}
              {isSubmitting ? 'Processing…' : `Pay ${formatRupiah(orderTotal)}`}
            </button>
          </div>
        </div>
      )}
    </main>
  </div>
);
}
